/**
 * Wahl des Transports für Bon und Kassenlade (Android-Hülle, Codex Runde 3 Fund 2): genau ein Weg
 * je Vorgang – nativ über `window.BilanxGeraet` (nur mit reiner Bonquelle, Server-Wunsch S-1) oder
 * wie bisher über Server und Hardware-Bridge. Ohne Bonquelle bleibt der Bondruck beim Server-Weg;
 * nur die Lade läuft dann nativ.
 */
import type { Speicher } from './belegnummern';
import {
  DruckErgebnisUnklar,
  druckeBeleg,
  type DruckApi,
  type GeraeteRueckmeldung,
} from './bondruck';
import {
  druckeBelegAmGeraet,
  druckversuchQuelle,
  druckweg,
  type DruckversuchHttp,
  oeffneLadeAmGeraet,
  type BilanxGeraet,
  type BonQuelle,
  type DruckversuchQuelle,
} from './geraet';

/** Bondruck für Belegansicht/Belege: `auto` = einmaliger Druck nach dem Verkauf, sonst Nachdruck-Taste. */
export type BonDruck = (belegnummer: string, opt?: { auto: boolean }) => Promise<string>;

/** Rückmeldung → erfüllt bei „gedruckt“, sonst Ausnahme (unklar als [DruckErgebnisUnklar]). */
function alsVersprechen(r: GeraeteRueckmeldung): string {
  if (r.ok === false) {
    throw new Error(r.text);
  }
  if (r.ok === null) {
    throw new DruckErgebnisUnklar(r.text);
  }
  return r.text;
}

export function erstelleBonDruck(opt: {
  api: DruckApi;
  speicher: Speicher;
  kassenId: string;
  bridgeZugelassen: boolean;
  geraet: BilanxGeraet | null;
  /** S-1/F6: Druckversuchsregister (nur mit Server-Signal `nativdruck`), sonst null = Bridge-Weg. */
  bonQuelle: BonQuelle | DruckversuchQuelle | null;
}): BonDruck | undefined {
  const { api, speicher, kassenId, bridgeZugelassen, geraet, bonQuelle } = opt;
  if (kassenId === '') {
    return undefined;
  }
  if (druckweg(geraet, bonQuelle) === 'nativ' && geraet !== null && bonQuelle !== null) {
    // Doppeltes Antippen: je Beleg läuft höchstens ein nativer Druck zugleich – ein zweiter
    // Aufruf wird abgewiesen, statt einen zweiten Versuch (Kopie) zu reservieren.
    const laufend = new Set<string>();
    return (belegnummer, o = { auto: false }) => {
      if (laufend.has(belegnummer)) {
        return Promise.reject(
          new Error('Bon wird für diesen Beleg schon gedruckt – Ergebnis abwarten'),
        );
      }
      laufend.add(belegnummer);
      return druckeBelegAmGeraet(geraet, bonQuelle, speicher, kassenId, belegnummer, {
        art: o.auto ? 'auto' : 'nachdruck',
      })
        .finally(() => {
          laufend.delete(belegnummer);
        })
        .then(alsVersprechen);
    };
  }
  if (!bridgeZugelassen) {
    return undefined;
  }
  // Heutiger Weg; Nachdruck eines gedruckten Belegs = „KOPIE“.
  return (belegnummer) => druckeBeleg(api, speicher, kassenId, belegnummer).then(alsVersprechen);
}

/** Kassenlade über die native Bridge; undefined → Server-Weg (`oeffneLade`). */
export function erstelleLadeAmGeraet(
  geraet: BilanxGeraet | null,
): ((schluessel: string) => Promise<GeraeteRueckmeldung>) | undefined {
  return geraet === null ? undefined : (schluessel) => oeffneLadeAmGeraet(geraet, schluessel);
}

/**
 * S-1/F6: Bonquelle der Kasse. Das Druckversuchsregister gibt es nur, wenn
 * - diese Seite in der Android-Hülle läuft (`geraet`),
 * - der Server im Status ausdrücklich `nativdruck: true` meldet (Routen registriert, Modus `nativ`),
 * - eine Kasse eingerichtet ist.
 * Sonst null: der Bon läuft genau wie vor F6 (Server + Hardware-Bridge). Es wird nichts geraten –
 * ein fehlendes oder anderes Signal heißt immer „kein Nativdruck“.
 *
 * [kennung] liefert die beim Server für diese Kasse registrierte N1-Gerätekennung (in `App.tsx`
 * `kennungAbgleichen`); sie bildet mit dem Kassenschlüssel die Gerätebindung des Versuchs.
 */
export function nativeBonQuelle(opt: {
  geraet: BilanxGeraet | null;
  nativdruck: unknown;
  kassenId: string;
  http: DruckversuchHttp;
  kennung: (kassenId: string) => Promise<string>;
}): DruckversuchQuelle | null {
  if (opt.geraet === null || opt.nativdruck !== true || opt.kassenId === '') {
    return null;
  }
  return druckversuchQuelle(opt.http, opt.kennung);
}
